/**
 * 튜닝 채점 호출 — 화면과 백엔드 사이의 얇은 층.
 *
 * ## 채점과 재심은 **다른 경로**다
 *
 *  · 채점   `/challenge/grade-tuning`      — 논리 읽기 비율로 점수를 낸다. **AI 가 없어도 된다.**
 *  · 재심   `/assistant/challenge/tuning-review` — 사용자가 버튼을 눌렀을 때만. AI 경로다.
 *
 * 이 구분이 흐려지면 AI 제외 빌드에서 채점까지 막힌다. 그래서 재심만 `AI_ENABLED` 로 가린다.
 *
 * ## 문항 정의를 화면이 실어 보내는 이유
 *
 * 챌린지는 QBase 에 있을 수도 로컬에 있을 수도 있는데, QBase 것은 **브라우저가 직접 읽는다** —
 * 서버가 문항을 꺼내 볼 길이 없다. 그래서 모범답안과 허용 배수를 화면이 보낸다.
 * 기존 001 채점도 같은 모양이다(정답 SQL 을 브라우저가 받아 실행한다). 새로 생기는 위험은 없다.
 */
import axios from 'axios';
import i18n from '../i18n';
import { AI_ENABLED } from '../config/features';
import { loadSection } from '../utils/appSettings';
import { tuningGradingTarget } from './challengeDb';

const API = process.env.REACT_APP_API_BASE_URL || '/sql-developer';

/** 문항의 `CRANS_RST_CN` 에 담긴 튜닝 설정. 없거나 깨졌으면 빈 객체 — 화면이 죽지 않게. */
export function tuningConfigOf(quiz) {
  try {
    const raw = quiz?.CRANS_RST_CN || quiz?.cransRstCn;
    if (!raw) return {};
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
}

/** 이 채점 한 건을 가리키는 id. 중지 버튼이 이것으로 서버의 세션을 찾는다. */
export function newGradeId() {
  return `grd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * 채점을 요청한다.
 *
 * @param {object} a
 *   - gradeId       중지에 쓸 id
 *   - connectionId  잴 연결 (활성 연결)
 *   - kind          'sql' | 'index'
 *   - 003: answerSql, submittedSql
 *   - 004: probeSql, answerColumns, answerUnique, targetTable, submittedDdl
 *   - tolerance, partialFactor  문항이 정한 경계
 */
export async function gradeTuning(payload) {
  const res = await axios.post(`${API}/challenge/grade-tuning`, payload);
  return res.data;
}

/**
 * 문제를 고를 때의 사전 확인 — 대상 표가 이 연결에 있는가(004).
 * @returns {{ ready: boolean, reason?: 'no_connection'|'no_table', table?: string }}
 */
export async function checkTuningReadiness({ connectionId, targetTable, dbms }) {
  const res = await axios.post(`${API}/challenge/tuning-readiness`, { connectionId, targetTable, dbms });
  return res.data;
}

/**
 * 문제 쿼리의 실행계획 — 채점과 같은 DB·같은 측정. 웹은 connectionId 를 비운다(서버가 교육DB 로 잰다).
 * @returns {{ ok: true, planText: string, metrics: { buffers } }}
 */
export async function fetchTuningPlan({ connectionId, sql, dbms }) {
  const res = await axios.post(`${API}/challenge/tuning-plan`, { connectionId, sql, dbms });
  return res.data;
}

/**
 * 튜닝 과제 사전 검증(2026-10-06) — 편집기가 저장 전에 부른다. 서버가 채점과 같은 DB·같은 채점기로 모범답안(10점)·
 * 그대로 낸 답(0점)·개선 폭을 실제로 잰다(backend challenge/taskVerify.js). 대상은 채점과 같은 규칙(tuningGradingTarget).
 */
export async function verifyTuningTask(task, dbms) {
  const target = tuningGradingTarget();
  if (target.error) {
    const e = new Error(i18n.t('challenge:tuning.needConnection'));
    e.errorType = 'NO_CONNECTION';
    throw e;
  }
  const res = await axios.post(`${API}/challenge/verify-tuning-task`, { task, dbms, connectionId: target.connectionId });
  return res.data;
}

/** 평가 기준(루브릭) 값 — 채점과 같은 상수. 「평가 기준」 창이 읽는다 */
export async function fetchTuningRubric() {
  const res = await axios.get(`${API}/challenge/tuning-rubric`);
  return res.data;
}

/** 채점 중지. 실패해도 화면이 멈추면 안 되므로 조용히 삼킨다 — 어차피 결과로 드러난다. */
export async function cancelTuning(gradeId) {
  try {
    const res = await axios.post(`${API}/challenge/grade-tuning/${encodeURIComponent(gradeId)}/cancel`);
    return res.data;
  } catch (_e) {
    return { cancelled: false };
  }
}

/** 재심을 쓸 수 있는 빌드인가. AI 제외 빌드에서는 버튼을 아예 그리지 않는다. */
export const reviewAvailable = () => AI_ENABLED;

/**
 * 재심 요청 — **기계 점수가 먼저 있어야 한다.** 서버가 그것을 확인한다.
 * 모델이 규격을 벗어나면 서버가 기계 점수를 유지하고 `invalid: true` 를 준다.
 */
export async function requestReview(payload) {
  if (!AI_ENABLED) {
    // 버튼을 가렸어도 여기까지 오는 길이 생길 수 있다 — 같은 문구를 쓴다(assistantClient 와 동일)
    const e = new Error(i18n.t('common:errors.AI_NOT_INCLUDED'));
    e.errorType = 'AI_NOT_INCLUDED';
    throw e;
  }
  // **설정에서 고른 제공자를 싣는다.** 안 실으면 서버가 claude 로 정해, Codex 로 로그인한 사람이
  // 「연결된 인증이 없다」 를 받았다(지적 2026-10-05). 모니터링 창(AwrReportPanel 등)과 같은 읽기다
  const provider = (loadSection('assistant') || {}).provider || undefined;
  const res = await axios.post(`${API}/assistant/challenge/tuning-review`, { ...payload, provider });
  return res.data;
}
